// biome-ignore-all lint/style/useNamingConvention: keys are external CDF column identifiers
import { type ColumnType, numberCol, withUnit } from '#core/lib/expr/schema/types.ts';
import { type UnitSlug, unitForSlug } from '#core/lib/units/registry.ts';

/**
 * The distinct measurement categories a virtual-view column can carry. A column
 * declares its `kind`; {@link UNIT_BY_KIND} owns the category → stored-unit
 * mapping, so changing a unit is a single edit here rather than a sweep across
 * columns. ADNOC stores deterministic per-phase imperial units (gas Mscf,
 * oil/water stb); the `…Model` categories carry the MMscf convention used by
 * potential / target / network-model values. `unitless` is the explicit category
 * for labels, indices, statuses, flags, and dimensionless coefficients.
 */
export type MeasurementKind =
  | 'liquidVolume'
  | 'gasVolume'
  | 'gasVolumeModel'
  | 'liquidRate'
  | 'gasRate'
  | 'gasRateModel'
  | 'pressureAbsolute'
  | 'pressureDelta'
  | 'temperature'
  | 'temperatureDelta'
  | 'fraction'
  | 'gasOilRatio'
  | 'liquidGasRatio'
  | 'productivityIndex'
  | 'length'
  | 'velocity'
  | 'frequency'
  | 'pricePerBarrel'
  | 'money'
  | 'power'
  | 'current'
  | 'voltage'
  | 'apparentPower'
  | 'chokeSize'
  | 'durationDays'
  | 'unitless';

/**
 * Category → stored-unit slug — the single source of truth for column units.
 * Every shape column's declared {@link MeasurementKind} resolves through here;
 * `undefined` ⇒ the column renders without a unit. The per-phase imperial scales
 * (oil/water stb, allocated gas Mscf, model gas MMscf) follow ADNOC's
 * authoritative wiki unit tables.
 */
const UNIT_BY_KIND: Readonly<Record<MeasurementKind, UnitSlug | undefined>> = {
  liquidVolume: 'stb',
  gasVolume: 'mscf',
  gasVolumeModel: 'mmscf',
  liquidRate: 'stb_per_d',
  gasRate: 'mscf_per_d',
  gasRateModel: 'mmscf_per_d',
  pressureAbsolute: 'psig',
  pressureDelta: 'psi',
  temperature: 'degf',
  temperatureDelta: 'degc',
  fraction: 'pct',
  gasOilRatio: 'scf_per_stb',
  liquidGasRatio: 'stb_per_mmscf',
  productivityIndex: 'stb_per_d_psi',
  length: 'ft',
  velocity: 'ft_per_s',
  frequency: 'hz',
  pricePerBarrel: 'usd_per_bbl',
  money: 'usd',
  power: 'hp',
  current: 'a',
  voltage: 'v',
  apparentPower: 'kva',
  chokeSize: 'in',
  durationDays: 'd',
  unitless: undefined,
};

/** Resolve a column's {@link MeasurementKind} to its stored-unit slug, or `undefined` if unit-less. */
export const unitForKind = (kind: MeasurementKind): UnitSlug | undefined => UNIT_BY_KIND[kind];

/**
 * The numeric column type for a resolved unit slug: a unit-stamped `numberCol`,
 * or a bare `numberCol` when the column is unit-less. SSOT for the
 * "slug → numeric column type" derivation shared by the view factories.
 */
export const numericColForSlug = (slug: UnitSlug | undefined): ColumnType =>
  slug === undefined ? numberCol : withUnit(numberCol, unitForSlug(slug));
